import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { companyInfo, subsidiaries, contactInfo } from '../data/mock';
import { StarField, GlowingOrb } from './ui/StarField';

gsap.registerPlugin(ScrollTrigger);

// Hero Section - Full bleed image with StarField
const HeroSection = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.hero-content > *',
        { y: 80, opacity: 0 },
        { y: 0, opacity: 1, duration: 1.2, stagger: 0.2, ease: 'power4.out', delay: 0.3 }
      );
    }, heroRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={heroRef} className="relative h-screen overflow-hidden" data-testid="hero-section">
      {/* Background layers */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80"
          alt="Luxury skyline"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1929]/80 via-[#0B1929]/60 to-[#0B1929]" />
      </div>

      {/* Animated StarField */}
      <StarField />
      
      {/* Glowing Orbs */}
      <GlowingOrb className="top-1/4 left-1/4" size={500} color="#C9A84C" />
      <GlowingOrb className="bottom-1/4 right-1/4" size={400} color="#5BAED4" />

      {/* Content */}
      <div className="hero-content relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <div className="relative w-40 h-40 mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_95dc4b1c-bdc5-4cb5-95d0-cd8e4031f4d3/artifacts/2povbshx_Designer%20%281%29.png"
            alt="Beit Nour Group"
            className="w-full h-full object-contain"
            style={{ filter: 'drop-shadow(0 0 40px rgba(201, 168, 76, 0.4))' }}
            data-testid="hero-logo"
          />
        </div>
        
        <h1 className="text-5xl md:text-7xl lg:text-9xl font-heading font-bold tracking-tight">
          <span className="text-[#C9A84C]">Beit </span>
          <span className="text-[#5BAED4]">Nour </span>
          <span className="text-white">Group</span>
        </h1>
        
        <div className="mt-6 flex items-center gap-4">
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#C9A84C]/60" />
          <span className="text-[#C9A84C] text-sm md:text-base tracking-[0.4em] uppercase">{companyInfo.tagline}</span>
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#C9A84C]/60" />
        </div>
        
        <p className="mt-6 text-white/70 text-lg md:text-xl max-w-2xl">{companyInfo.subtitle}</p>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/40 text-xs tracking-[0.3em] uppercase">Scroll</span>
        <ChevronDown size={20} className="text-[#C9A84C]" />
      </div>
    </section>
  );
};

// About/Vision Section - Image background
const VisionSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.vision-content',
        { y: 60, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 70%' }
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-screen overflow-hidden" data-testid="vision-section">
      {/* Background image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80"
          alt="Global business"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1929]/95 via-[#0B1929]/80 to-[#0B1929]/60" />
      </div>

      <div className="vision-content relative z-10 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-[#C9A84C] text-sm tracking-[0.3em] uppercase mb-4 block">Our Vision</span>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold leading-tight">
              <span className="text-white">One Vision</span><br/>
              <span className="text-[#C9A84C]">Global Horizons</span>
            </h2>
            <p className="mt-8 text-white/70 text-lg leading-relaxed">{companyInfo.description}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-8">
            {[
              { value: '8', label: 'Ventures' },
              { value: '6+', label: 'Countries' },
              { value: '5', label: 'Industries' },
              { value: '3', label: 'Continents' },
            ].map((stat, i) => (
              <div key={i} className="text-center p-6 bg-white/5 backdrop-blur-sm rounded-2xl">
                <div className="text-5xl md:text-6xl font-heading font-bold text-[#C9A84C]">{stat.value}</div>
                <div className="text-white/60 text-sm tracking-[0.2em] uppercase mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Portfolio Section - Large image cards that blend
const PortfolioSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.portfolio-item',
        { y: 100, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, stagger: 0.15, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 60%' }
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative bg-[#0B1929] py-24" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-[#C9A84C] text-sm tracking-[0.3em] uppercase">Portfolio</span>
          <h2 className="mt-4 text-4xl md:text-6xl font-heading font-bold text-white">Our Ventures</h2>
        </div>

        {/* Large image grid - no boxes, images blend */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {subsidiaries.map((company, index) => (
            <a
              key={company.id}
              href={company.url || '#'}
              target={company.url ? '_blank' : '_self'}
              rel="noopener noreferrer"
              className={`portfolio-item group relative overflow-hidden ${index === 0 || index === 3 ? 'md:col-span-2 aspect-[21/9]' : 'aspect-[16/10]'}`}
              onClick={(e) => { if (!company.url) e.preventDefault(); }}
              data-testid={`portfolio-card-${index}`}
            >
              {/* Full bleed image */}
              <img 
                src={company.image}
                alt={company.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1929] via-[#0B1929]/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
              
              {/* Color accent line */}
              <div 
                className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-500"
                style={{ backgroundColor: company.color }}
              />

              {/* Content */}
              <div className="absolute inset-0 p-8 flex flex-col justify-end">
                {company.comingSoon && (
                  <span 
                    className="absolute top-6 right-6 px-4 py-2 text-xs tracking-wider uppercase backdrop-blur-sm"
                    style={{ backgroundColor: `${company.color}40`, color: company.color }}
                  >
                    Coming Soon
                  </span>
                )}
                
                <span className="text-xs tracking-[0.2em] uppercase mb-2" style={{ color: company.color }}>
                  {company.sector}
                </span>
                
                <h3 className="text-2xl md:text-3xl font-heading font-bold text-white group-hover:text-[#C9A84C] transition-colors">
                  {company.name}
                </h3>
                
                <p className="mt-2 text-white/60 text-sm max-w-lg line-clamp-2 group-hover:text-white/80 transition-colors">
                  {company.description}
                </p>
                
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-white/40 text-sm">{company.location}</span>
                  {company.url && (
                    <span className="flex items-center gap-2 text-sm group-hover:text-[#C9A84C] text-white/60 transition-colors">
                      Visit <ExternalLink size={14} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  )}
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Founder Section - Vogue Editorial Style
const FounderSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.founder-content',
        { x: -60, opacity: 0 },
        { x: 0, opacity: 1, duration: 1, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 60%' }
        }
      );
      gsap.fromTo('.founder-image-container',
        { x: 60, opacity: 0 },
        { x: 0, opacity: 1, duration: 1.2, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 60%' }
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-screen bg-[#0B1929] overflow-hidden" data-testid="founder-section">
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, #C9A84C 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 min-h-screen flex items-center">
        <div className="grid lg:grid-cols-2 gap-16 items-center w-full">
          
          {/* Left - Content with Vogue Style */}
          <div className="founder-content order-2 lg:order-1">
            {/* Editorial Label */}
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px w-12 bg-[#C9A84C]" />
              <span className="text-[#C9A84C] text-xs tracking-[0.5em] uppercase">The Visionary</span>
            </div>
            
            {/* Vogue Style Name */}
            <h2 className="text-5xl md:text-7xl lg:text-8xl font-heading font-bold italic text-white leading-none tracking-tight">
              Yitayal
            </h2>
            <h2 className="text-5xl md:text-7xl lg:text-8xl font-heading font-light text-[#C9A84C] leading-none tracking-tight mt-2">
              Menkir
            </h2>
            
            {/* Title */}
            <p className="mt-6 text-white/60 text-sm tracking-[0.3em] uppercase">
              Founder & Chairman
            </p>

            {/* Editorial Bio */}
            <p className="mt-8 text-white/70 text-lg leading-relaxed font-light italic max-w-lg">
              "A visionary entrepreneur with a passion for building businesses that create lasting impact across diverse industries and continents."
            </p>

            {/* Top 3 Professional Stats - Auditor Style */}
            <div className="mt-12 grid grid-cols-3 gap-6">
              <div className="border-l-2 border-[#C9A84C] pl-4">
                <div className="text-3xl md:text-4xl font-heading font-bold text-white">8</div>
                <div className="text-white/50 text-xs tracking-[0.2em] uppercase mt-1">Ventures<br/>Founded</div>
              </div>
              <div className="border-l-2 border-[#C9A84C] pl-4">
                <div className="text-3xl md:text-4xl font-heading font-bold text-white">6+</div>
                <div className="text-white/50 text-xs tracking-[0.2em] uppercase mt-1">Countries<br/>Operated</div>
              </div>
              <div className="border-l-2 border-[#C9A84C] pl-4">
                <div className="text-3xl md:text-4xl font-heading font-bold text-white">5</div>
                <div className="text-white/50 text-xs tracking-[0.2em] uppercase mt-1">Industries<br/>Disrupted</div>
              </div>
            </div>

            {/* Expertise Areas */}
            <div className="mt-10 flex flex-wrap gap-3">
              {['Real Estate', 'Technology', 'AI Innovation', 'Hospitality', 'Investment'].map((tag) => (
                <span key={tag} className="px-4 py-2 text-xs tracking-[0.15em] uppercase text-white/60 border border-white/10 hover:border-[#C9A84C]/50 transition-colors">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Right - Image */}
          <div className="founder-image-container order-1 lg:order-2 relative">
            {/* Frame decoration */}
            <div className="absolute -top-6 -right-6 w-full h-full border border-[#C9A84C]/30" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 border border-[#C9A84C]/20" />
            
            {/* Main Image */}
            <div className="relative overflow-hidden bg-[#0B1929]">
              <img
                src={companyInfo.founderImage}
                alt={companyInfo.founderName}
                className="w-full h-auto object-cover grayscale hover:grayscale-0 transition-all duration-700"
                style={{ maxHeight: '700px' }}
                data-testid="founder-image"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1929]/40 to-transparent" />
            </div>
            
            {/* Caption */}
            <div className="absolute bottom-8 left-8 right-8">
              <div className="h-px w-full bg-gradient-to-r from-[#C9A84C] to-transparent mb-4" />
              <p className="text-white/40 text-xs tracking-[0.2em] uppercase">
                Dubai • Bali • Zurich • Africa
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Global Presence - Image backgrounds for each location
const GlobalPresenceSection = () => {
  const sectionRef = useRef(null);
  
  const locations = [
    { city: 'Dubai', country: 'UAE', image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80' },
    { city: 'Bali', country: 'Indonesia', image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800&q=80' },
    { city: 'Zurich', country: 'Switzerland', image: 'https://images.unsplash.com/photo-1515488764276-beab7607c1e6?w=800&q=80' },
    { city: 'Ibiza', country: 'Spain', image: 'https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=800&q=80', soon: true },
    { city: 'Zanzibar', country: 'Tanzania', image: 'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=800&q=80', soon: true },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.location-card',
        { y: 60, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 60%' }
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-24 bg-[#0B1929]" data-testid="global-presence-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-[#C9A84C] text-sm tracking-[0.3em] uppercase">Global Presence</span>
          <h2 className="mt-4 text-4xl md:text-6xl font-heading font-bold">
            <span className="text-white">From </span>
            <span className="text-[#5BAED4]">Dubai </span>
            <span className="text-white">to the </span>
            <span className="text-[#C9A84C]">World</span>
          </h2>
        </div>

        {/* Location images - no boxes */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {locations.map((loc, i) => (
            <div 
              key={i}
              className="location-card group relative aspect-[3/4] overflow-hidden cursor-pointer"
            >
              <img 
                src={loc.image}
                alt={loc.city}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1929] via-[#0B1929]/30 to-transparent" />
              
              <div className="absolute inset-0 flex flex-col items-center justify-end p-6 text-center">
                {loc.soon && (
                  <span className="text-[#C9A84C]/80 text-xs tracking-wider uppercase mb-2">Coming Soon</span>
                )}
                <h3 className="text-2xl font-heading font-bold text-white">{loc.city}</h3>
                <p className="text-white/60 text-sm">{loc.country}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section - Full bleed image
const ContactSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.contact-content > *',
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, stagger: 0.15, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 60%' }
        }
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-screen overflow-hidden" data-testid="contact-section">
      {/* Background image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1920&q=80"
          alt="Modern office"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0B1929]/80" />
      </div>

      <div className="contact-content relative z-10 min-h-screen flex flex-col items-center justify-center text-center px-6">
        <span className="text-[#C9A84C] text-sm tracking-[0.3em] uppercase mb-4">Connect</span>
        
        <h2 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold">
          <span className="text-white">Let's Build</span><br/>
          <span className="text-[#C9A84C]">Together</span>
        </h2>
        
        <p className="mt-6 text-white/70 text-lg max-w-xl">
          We collaborate with visionary partners, investors, and institutions to build transformative ventures across the globe.
        </p>
        
        <a
          href={`mailto:${contactInfo.email}`}
          className="mt-10 inline-flex items-center gap-3 px-10 py-5 bg-[#C9A84C] hover:bg-[#D4B85E] text-[#0B1929] rounded-full text-base tracking-[0.1em] uppercase font-semibold transition-all duration-300 hover:scale-105"
          data-testid="contact-email-btn"
        >
          Get in Touch <span>→</span>
        </a>
        
        <p className="mt-4 text-white/40 text-sm">{contactInfo.email}</p>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="relative py-8 bg-[#0B1929] border-t border-white/5" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <img
          src="https://customer-assets.emergentagent.com/job_95dc4b1c-bdc5-4cb5-95d0-cd8e4031f4d3/artifacts/2povbshx_Designer%20%281%29.png"
          alt="Beit Nour Group"
          className="w-10 h-10 object-contain"
        />
        <span className="text-white font-heading font-semibold">Beit Nour Group</span>
      </div>
      <p className="text-white/40 text-sm">© {new Date().getFullYear()} Beit Nour Group. All rights reserved.</p>
    </div>
  </footer>
);

// Main Component
export const ImmersivePage = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
    return () => ScrollTrigger.getAll().forEach(t => t.kill());
  }, []);

  return (
    <div className={`bg-[#0B1929] transition-opacity duration-1000 ${loaded ? 'opacity-100' : 'opacity-0'}`}>
      <HeroSection />
      <VisionSection />
      <PortfolioSection />
      <FounderSection />
      <GlobalPresenceSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

export default ImmersivePage;
